import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  CircularProgress, 
  Button, 
  SvgIcon,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Container,
  Fade,
  Grow
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  Tour as TourIcon, 
  Article as ArticleIcon, 
  Group as GroupIcon, 
  BookOnline as BookOnlineIcon,
  Message as MessageIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckIcon,
  Schedule as PendingIcon,
  AttachMoney as MoneyIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  Analytics as AnalyticsIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon
} from '@mui/icons-material';

import { tourService } from '../../services/firebase';
import { blogService } from '../../services/firebase';
import { userService } from '../../services/firebase';
import { bookingService } from '../../services/firebase';
import { contactPageService } from '../../services/firebase';
import { customTourRequestService } from '../../services/firebase';
import { CustomTourRequest } from '../../types';
// languageSettings moved to Site Settings page

interface StatCardProps {
  title: string;
  count: number;
  icon: React.ReactElement;
  gradient?: string;
  delay?: number;
}

const ModernStatCard: React.FC<StatCardProps & { onClick?: () => void }> = ({ title, count, icon, gradient, delay = 0, onClick }) => (
  <Grow in={true} style={{ transformOrigin: '0 0 0' }} timeout={1000 + delay * 200}>
    <Card 
      className="admin-stat-card"
      onClick={onClick}
      sx={{ 
        height: '100%',
        background: gradient || 'var(--color-beyaz)',
        color: gradient ? 'white' : 'inherit',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: onClick ? 'translateY(-12px) scale(1.03)' : 'translateY(-8px) scale(1.02)',
          boxShadow: onClick ? '0 20px 40px rgba(0,0,0,0.2)' : '0 15px 30px rgba(0,0,0,0.15)',
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                color: gradient ? 'rgba(255,255,255,0.9)' : 'text.secondary',
                fontWeight: 500,
                mb: 1
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h3" 
              component="p" 
              sx={{ 
                fontWeight: 700,
                color: gradient ? 'white' : 'var(--color-lacivert)'
              }}
            >
              {count.toLocaleString('tr-TR')}
            </Typography>
          </Box>
          <Box sx={{ 
            p: 2, 
            borderRadius: '50%', 
            backgroundColor: gradient ? 'rgba(255,255,255,0.2)' : 'var(--color-acik-gri)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <SvgIcon sx={{ 
              fontSize: 40, 
              color: gradient ? 'white' : 'var(--color-acik-mavi)' 
            }}>
              {icon}
            </SvgIcon>
          </Box>
        </Box>
      </CardContent>
    </Card>
  </Grow>
);

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ tours: 0, blogs: 0, users: 0, bookings: 0, messages: 0 });
  const [bookingStats, setBookingStats] = useState({ 
    pending: 0, 
    confirmed: 0, 
    completed: 0, 
    cancelled: 0, 
    totalRevenue: 0, 
    pendingPayments: 0 
  });
  const [loading, setLoading] = useState(true);
  const [contactMessages, setContactMessages] = useState<any[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [customTourRequests, setCustomTourRequests] = useState<CustomTourRequest[]>([]);

  // dil ayarları bu sayfadan kaldırıldı

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Tarih bilinmiyor';
    
    try {
      let date: Date;
      
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else {
        return 'Geçersiz tarih';
      }
      
      return date.toLocaleDateString('tr-TR');
    } catch (error) {
      console.error('Date formatting error:', error, timestamp);
      return 'Tarih hatası';
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const [toursCount, blogsCount, usersCount, bookingsCount, messages, allBookings] = await Promise.all([
          tourService.getToursCount(),
          blogService.getBlogsCount(),
          userService.getUsersCount(),
          bookingService.getBookingsCount(),
          contactPageService.getContactMessages(),
          bookingService.getAllBookings()
        ]);
        
        // Calculate booking statistics
        const pendingBookings = allBookings.filter(b => b.status === 'pending').length;
        const confirmedBookings = allBookings.filter(b => b.status === 'confirmed').length;
        const completedBookings = allBookings.filter(b => b.status === 'completed').length;
        const cancelledBookings = allBookings.filter(b => b.status === 'cancelled').length;
        const totalRevenue = allBookings
          .filter(b => b.paymentStatus === 'paid')
          .reduce((sum, b) => sum + b.totalPrice, 0);
        const pendingPayments = allBookings
          .filter(b => b.paymentStatus === 'pending')
          .reduce((sum, b) => sum + b.totalPrice, 0);
        
        setStats({ 
          tours: toursCount, 
          blogs: blogsCount, 
          users: usersCount, 
          bookings: bookingsCount, 
          messages: messages.length 
        });
        
        setBookingStats({
          pending: pendingBookings,
          confirmed: confirmedBookings,
          completed: completedBookings,
          cancelled: cancelledBookings,
          totalRevenue,
          pendingPayments
        });
        
        setContactMessages(messages);
      } catch (error) {
        console.error("İstatistikler yüklenirken hata oluştu:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
    fetchCustomTourRequests();
  }, []);

  // kaldırıldı

  const fetchCustomTourRequests = async () => {
    try {
      const allRequests = await customTourRequestService.getCustomTourRequests();
      setCustomTourRequests(allRequests);
    } catch (err) {
      // Hata yönetimi
    }
  };

  const handleViewMessage = (message: any) => {
    setSelectedMessage(message);
    setMessageDialogOpen(true);
  };

  const handleMarkMessageAsRead = async (messageId: string) => {
    try {
      await contactPageService.markMessageAsRead(messageId);
      // Refresh messages
      const messages = await contactPageService.getContactMessages();
      setContactMessages(messages);
      setStats(prev => ({ ...prev, messages: messages.length }));
    } catch (err) {
      console.error('Mesaj okundu olarak işaretlenirken hata:', err);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} sx={{ color: 'var(--color-acik-mavi)' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      
      
      {/* Ana İstatistikler */}
      <Box sx={{ mb: 6 }}>
        <Typography 
          className="admin-section-title" 
          variant="h5"
          sx={{
            fontWeight: 700,
            color: 'var(--color-lacivert)',
            mb: 3,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-8px',
              left: 0,
              width: '60px',
              height: '4px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '2px'
            }
          }}
        >
          Genel İstatistikler
        </Typography>
         <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 3 }}>
           <ModernStatCard 
             title="Toplam Tur" 
             count={stats.tours} 
             icon={<TourIcon />} 
             gradient="var(--gradient-primary)"
             delay={0}
             onClick={() => navigate('/admin/tours')}
           />
           <ModernStatCard 
             title="Toplam Blog" 
             count={stats.blogs} 
             icon={<ArticleIcon />} 
             gradient="var(--gradient-info)"
             delay={1}
             onClick={() => navigate('/admin/blogs')}
           />
           <ModernStatCard 
             title="Toplam Kullanıcı" 
             count={stats.users} 
             icon={<GroupIcon />} 
             gradient="var(--gradient-success)"
             delay={2}
             onClick={() => navigate('/admin/users')}
           />
           <ModernStatCard 
             title="Toplam Rezervasyon" 
             count={stats.bookings} 
             icon={<BookOnlineIcon />} 
             gradient="var(--gradient-warning)"
             delay={3}
             onClick={() => navigate('/admin/bookings')}
           />
           <ModernStatCard 
             title="Toplam Mesaj" 
             count={stats.messages} 
             icon={<MessageIcon />} 
             gradient="linear-gradient(135deg, #e74c3c 0%, #f39c12 100%)"
             delay={4}
             onClick={() => navigate('/admin/messages')}
           />
           <Box sx={{ flex: '1 1 25%', minWidth: 220, p: 1 }}>
             <ModernStatCard
               title="Özel Tur Talepleri"
               count={customTourRequests.length}
               icon={<TourIcon />}
               gradient="linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
               delay={5}
               onClick={() => navigate('/admin/custom-tour-requests')}
             />
           </Box>
           <Box sx={{ flex: '1 1 25%', minWidth: 220, p: 1 }}>
             <ModernStatCard
               title="SEO Test"
               count={0}
               icon={<SearchIcon />}
               gradient="linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)"
               delay={6}
               onClick={() => navigate('/admin/seo-test')}
             />
           </Box>
         </Box>
      </Box>

      {/* Rezervasyon İstatistikleri */}
      <Box sx={{ mb: 6 }}>
        <Typography 
          className="admin-section-title" 
          variant="h5"
          sx={{
            fontWeight: 700,
            color: 'var(--color-lacivert)',
            mb: 3,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-8px',
              left: 0,
              width: '60px',
              height: '4px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '2px'
            }
          }}
        >
          Rezervasyon Detayları
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 3 }}>
          <ModernStatCard 
            title="Bekleyen Rezervasyonlar" 
            count={bookingStats.pending} 
            icon={<PendingIcon />} 
            delay={0}
            onClick={() => navigate('/admin/bookings')}
          />
          <ModernStatCard 
            title="Onaylı Rezervasyonlar" 
            count={bookingStats.confirmed} 
            icon={<CheckIcon />} 
            delay={1}
            onClick={() => navigate('/admin/bookings')}
          />
          <ModernStatCard 
            title="Tamamlanan Rezervasyonlar" 
            count={bookingStats.completed} 
            icon={<TrendingUpIcon />} 
            delay={2}
            onClick={() => navigate('/admin/bookings')}
          />
          <ModernStatCard 
            title="İptal Edilen" 
            count={bookingStats.cancelled} 
            icon={<CancelIcon />} 
            delay={3}
            onClick={() => navigate('/admin/bookings')}
          />
          <Card 
            className="admin-gradient-card" 
            onClick={() => navigate('/admin/bookings')}
            sx={{ 
              background: 'var(--gradient-success)',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-12px) scale(1.03)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                    Toplam Gelir
                  </Typography>
                  <Typography variant="h4" component="p" sx={{ color: 'white', fontWeight: 700 }}>
                    ₺{bookingStats.totalRevenue.toLocaleString('tr-TR')}
                  </Typography>
                </Box>
                <Box sx={{ 
                  p: 2, 
                  borderRadius: '50%', 
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography sx={{ fontSize: 32, color: 'white', fontWeight: 700 }}>₺</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card 
            className="admin-gradient-card" 
            onClick={() => navigate('/admin/bookings')}
            sx={{ 
              background: 'var(--gradient-warning)',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-12px) scale(1.03)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                    Bekleyen Ödemeler
                  </Typography>
                  <Typography variant="h4" component="p" sx={{ color: 'white', fontWeight: 700 }}>
                    ₺{bookingStats.pendingPayments.toLocaleString('tr-TR')}
                  </Typography>
                </Box>
                <Box sx={{ 
                  p: 2, 
                  borderRadius: '50%', 
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography sx={{ fontSize: 32, color: 'white', fontWeight: 700 }}>₺</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Mesajlar ve Analitik Bölümü */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 4 }}>
        <Box>
          <Card className="admin-stat-card">
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <NotificationsIcon sx={{ color: 'var(--color-acik-mavi)' }} />
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                    Son Gelen Mesajlar
                  </Typography>
                </Box>
                <Chip 
                  label={`${contactMessages.filter(m => !m.isRead).length} Okunmamış`} 
                  color={contactMessages.filter(m => !m.isRead).length > 0 ? 'warning' : 'success'}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              
              {contactMessages.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <MessageIcon sx={{ fontSize: 60, color: 'var(--color-gri-golge)', mb: 2 }} />
                  <Typography color="text.secondary">Henüz mesaj bulunmuyor.</Typography>
                </Box>
              ) : (
                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {contactMessages.slice(0, 5).map((message, index) => (
                    <React.Fragment key={message.id}>
                      <ListItem 
                        sx={{ 
                          backgroundColor: message.isRead ? 'transparent' : 'rgba(0, 91, 189, 0.05)',
                          borderRadius: 2,
                          mb: 1,
                          border: message.isRead ? 'none' : '1px solid rgba(0, 91, 189, 0.1)',
                          transition: 'var(--transition-normal)',
                          '&:hover': {
                            backgroundColor: 'var(--color-hover)',
                            transform: 'translateX(4px)'
                          }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Box component="div" sx={{ fontWeight: message.isRead ? 500 : 700, fontSize: '1rem', lineHeight: 1.5 }}>
                                {message.name}
                              </Box>
                              {!message.isRead && (
                                <Chip label="Yeni" color="primary" size="small" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box component="div">
                              <Box component="div" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.875rem' }}>
                                {message.subject || 'Konu belirtilmemiş'}
                              </Box>
                              <Box component="div" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                {formatTimestamp(message.createdAt)}
                              </Box>
                            </Box>
                          }
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Mesajı Görüntüle">
                            <IconButton 
                              size="small" 
                              onClick={() => handleViewMessage(message)}
                              sx={{ 
                                backgroundColor: 'var(--color-acik-mavi)',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: 'var(--color-kobalt-mavi)',
                                }
                              }}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          {!message.isRead && (
                            <Tooltip title="Okundu Olarak İşaretle">
                              <IconButton 
                                size="small" 
                                onClick={() => handleMarkMessageAsRead(message.id)}
                                sx={{ 
                                  backgroundColor: 'var(--color-success)',
                                  color: 'white',
                                  '&:hover': {
                                    backgroundColor: '#20C997',
                                  }
                                }}
                              >
                                <CheckIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </ListItem>
                      {index < contactMessages.slice(0, 5).length - 1 && <Divider sx={{ my: 1 }} />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
            {contactMessages.length > 5 && (
              <CardActions sx={{ p: 3, pt: 0 }}>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/admin/messages')}
                  sx={{ 
                    borderColor: 'var(--color-acik-mavi)',
                    color: 'var(--color-acik-mavi)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'var(--color-acik-mavi)',
                      color: 'white',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(0,91,189,0.3)'
                    }
                  }}
                >
                  Tüm Mesajları Görüntüle
                </Button>
              </CardActions>
            )}
          </Card>
        </Box>

        <Box>
          <Card 
            className="admin-stat-card"
            onClick={() => navigate('/admin/messages')}
            sx={{
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-8px) scale(1.02)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <AnalyticsIcon sx={{ color: 'var(--color-acik-mavi)' }} />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Mesaj Analitikleri
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 500 }}>Toplam Mesaj:</Typography>
                  <Chip 
                    label={contactMessages.length} 
                    sx={{ 
                      background: 'var(--gradient-primary)',
                      color: 'white',
                      fontWeight: 600
                    }} 
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 500 }}>Okunmamış:</Typography>
                  <Chip 
                    label={contactMessages.filter(m => !m.isRead).length} 
                    color={contactMessages.filter(m => !m.isRead).length > 0 ? 'warning' : 'success'}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 500 }}>Okunmuş:</Typography>
                  <Chip 
                    label={contactMessages.filter(m => m.isRead).length} 
                    color="success"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 500 }}>Bu Ay:</Typography>
                  <Chip 
                    label={contactMessages.filter(m => {
                      try {
                        let messageDate: Date | null = null;
                        
                        if (m.createdAt?.toDate && typeof m.createdAt.toDate === 'function') {
                          messageDate = m.createdAt.toDate();
                        } else if (m.createdAt instanceof Date) {
                          messageDate = m.createdAt;
                        } else if (m.createdAt?.seconds) {
                          messageDate = new Date(m.createdAt.seconds * 1000);
                        }
                        
                        if (!messageDate) return false;
                        
                        const now = new Date();
                        return messageDate.getMonth() === now.getMonth() && 
                               messageDate.getFullYear() === now.getFullYear();
                      } catch (error) {
                        return false;
                      }
                    }).length} 
                    color="info"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Message Detail Dialog */}
      <Dialog 
        open={messageDialogOpen} 
        onClose={() => setMessageDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: 'var(--shadow-xl)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'var(--gradient-primary)',
          color: 'white',
          borderRadius: '16px 16px 0 0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Mesaj Detayı</Typography>
            {selectedMessage && !selectedMessage.isRead && (
              <Chip label="Okunmamış" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} size="small" />
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedMessage && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Gönderen:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedMessage.name}</Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>E-posta:</Typography>
                <Typography variant="body1">{selectedMessage.email}</Typography>
              </Box>
              
              {selectedMessage.phone && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Telefon:</Typography>
                  <Typography variant="body1">{selectedMessage.phone}</Typography>
                </Box>
              )}
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Konu:</Typography>
                <Typography variant="body1">{selectedMessage.subject || 'Konu belirtilmemiş'}</Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Tarih:</Typography>
                <Typography variant="body1">
                  {(() => {
                    try {
                      if (!selectedMessage.createdAt) return 'Tarih bilinmiyor';
                      
                      let date: Date;
                      if (selectedMessage.createdAt.toDate && typeof selectedMessage.createdAt.toDate === 'function') {
                        date = selectedMessage.createdAt.toDate();
                      } else if (selectedMessage.createdAt instanceof Date) {
                        date = selectedMessage.createdAt;
                      } else if (selectedMessage.createdAt.seconds) {
                        date = new Date(selectedMessage.createdAt.seconds * 1000);
                      } else {
                        return 'Geçersiz tarih';
                      }
                      
                      return date.toLocaleString('tr-TR');
                    } catch (error) {
                      return 'Tarih hatası';
                    }
                  })()}
                </Typography>
              </Box>
              
              <Divider />
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>Mesaj:</Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    backgroundColor: 'var(--color-acik-gri)',
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid var(--color-border)'
                  }}
                >
                  {selectedMessage.message}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setMessageDialogOpen(false)}
            variant="outlined"
            sx={{ 
              borderRadius: '8px',
              textTransform: 'none'
            }}
          >
            Kapat
          </Button>
          {selectedMessage && !selectedMessage.isRead && (
            <Button 
              onClick={() => {
                handleMarkMessageAsRead(selectedMessage.id);
                setMessageDialogOpen(false);
              }}
              variant="contained"
              sx={{ 
                background: 'var(--gradient-primary)',
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  background: 'var(--gradient-primary)',
                  opacity: 0.9
                }
              }}
            >
              Okundu Olarak İşaretle
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dil Ayarları kaldırıldı */}

      {/* Son Özel Tur Talepleri */}
      <Box mb={5}>
        <Typography className="admin-section-title" variant="h5" mb={2}>
          Son Özel Tur Talepleri
        </Typography>
        <Card sx={{ p: 0, borderRadius: 3, boxShadow: 'var(--shadow-md)' }}>
          <CardContent sx={{ p: 0 }}>
            {customTourRequests.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">Henüz özel tur talebi yok.</Typography>
              </Box>
            ) : (
              <List disablePadding>
                {customTourRequests.slice(0, 5).map((req) => (
                  <ListItem key={req.id} alignItems="flex-start" sx={{ borderBottom: '1px solid #f0f0f0', py: 2, px: 3 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                        {req.tourType} <Chip label={req.status === 'pending' ? 'Beklemede' : req.status === 'contacted' ? 'İletişime Geçildi' : req.status === 'quoted' ? 'Teklif Verildi' : req.status === 'booked' ? 'Rezervasyon Yapıldı' : 'İptal Edildi'} size="small" color={req.status === 'pending' ? 'warning' : req.status === 'contacted' ? 'info' : req.status === 'quoted' ? 'primary' : req.status === 'booked' ? 'success' : 'error'} sx={{ ml: 1, fontWeight: 600 }} />
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {req.destination} | {req.preferredDates}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Talep Tarihi: {req.createdAt?.toDate?.().toLocaleDateString?.('tr-TR') || ''}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {req.message}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default AdminDashboardPage; 